import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { SpecificationEmbodimentOutlineService } from '../specification-embodiment-outline-service';
import type { Deferred } from '@patsnap/tahiti-core/utils/deferred';
import { create } from '@patsnap/tahiti-core';
import { CreateEmbodimentOutlineApi, type EmbodimentSummary, type EmbodimentOutline } from '../../apis/create-embodiment-outline-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { SpecificationEmbodimentOutlineModel } from '../../models/specification-embodiment-outline-model';
import _ from 'lodash';

@injectable()
export class SpecificationEmbodimentOutlineServiceImpl implements SpecificationEmbodimentOutlineService {
    creatingDeferred: Deferred<any> | undefined;

    constructor(
        @inject(CreateEmbodimentOutlineApi)
        private _createEmbodimentOutlineApi: CreateEmbodimentOutlineApi,
        @inject(CurrentTaskService)
        private _currentTaskService: CurrentTaskService,
        @inject(SpecificationEmbodimentOutlineModel)
        public model: SpecificationEmbodimentOutlineModel,
    ) {}

    reset() {
        this.model.update(() => ({
            outlines: [],
            loading: true,
            summary: undefined,
            conclusion: undefined,
            disabled: true,
        }));
    }

    get taskId(): string {
        const taskId = this._currentTaskService.taskId;
        if (!taskId) {
            throw new Error('当前任务ID不存在');
        }
        return taskId;
    }

    enter(): void {
        this.creatingDeferred = create();
        this.init();
    }

    finish(): void {
        this.creatingDeferred && this.creatingDeferred.resolve({});
    }

    abort(): void {
        this.creatingDeferred && this.creatingDeferred.reject();
        this.model.update(state => ({ ...state, loading: false, disabled: true }));
    }

    private _readFromApi(outlines: (EmbodimentOutline | EmbodimentSummary)[]) {
        const summary = outlines[0] as EmbodimentSummary;
        const conclusion = outlines[outlines.length - 1] as EmbodimentSummary;
        const outlineContent = outlines.slice(1, -1) as EmbodimentOutline[];

        this.model.update(state => ({
            ...state,
            outlines: outlineContent,
            summary,
            conclusion,
        }));
    }

    async pass() {
        this.reset();
        try {
            const res = await this._createEmbodimentOutlineApi.getEmbodimentOutline(this.taskId);
            this._readFromApi(res.outlines);
        } finally {
            // 暂时不用做什么
        }
    }

    async init(): Promise<void> {
        this.reset();
        try {
            const res = await this._createEmbodimentOutlineApi.createEmbodimentOutline({ task_id: this.taskId });
            this._readFromApi(res.outlines);
        } finally {
            this.model.update(state => ({ ...state, loading: false, disabled: false }));
        }
    }

    async submit(): Promise<void> {
        await this.save();
        this.finish();
    }

    async save() {
        const currentState = this.model.modelData;
        if (!currentState.outlines?.length) {
            throw new Error('实施例大纲为空，无法保存');
        }

        // 组合完整的 outlines 数组：概要 + 大纲内容 + 总结
        const fullOutlines: (EmbodimentOutline | EmbodimentSummary)[] = [];

        // 添加概要
        if (currentState.summary) {
            fullOutlines.push(currentState.summary);
        }

        // 添加大纲内容
        fullOutlines.push(...currentState.outlines);

        // 添加总结
        if (currentState.conclusion) {
            fullOutlines.push(currentState.conclusion);
        }

        await this._createEmbodimentOutlineApi.submitEmbodimentOutline(this.taskId, {
            content: {
                outlines: fullOutlines,
            },
        });
    }

    async append(embodimentOutline: EmbodimentOutline): Promise<EmbodimentOutline> {
        const res = await this._createEmbodimentOutlineApi.modifyEmbodimentOutline({
            task_id: this.taskId,
            claim_text: embodimentOutline.claim_text || '',
            claim_numbers: embodimentOutline.claim_numbers || [],
        });
        return _.get(res, 'outlines[0]', null) as EmbodimentOutline;
    }

    async remove(embodimentOutline: EmbodimentOutline) {
        this.model.update(state => {
            const outlineContent = state.outlines.filter(outline => outline.embodiment_number !== embodimentOutline.embodiment_number);
            return {
                ...state,
                outlines: outlineContent,
            };
        });
        await this.save();
    }
}
